import { Chain } from '@1delta/chain-registry'
import {
  bitwayFetcher,
  bitwayYieldKey,
} from '../../yields/intrinsic/fetchers/bitway'
import type { SavingsRegistryEntry } from './registry'

/**
 * Bitway Earn rows (BNB — see BITWAY.md at the repo root for the full
 * assessment). Two multi-token staking vaults; users stake a stablecoin
 * (or BTW) and receive a per-leg LP token whose rate accrues at a FIXED
 * admin-set reward dial (`getCurrentRewardRate`, validated against
 * realized 18-day accrual 2026-08-19), while the capital runs
 * market-neutral strategies in CEX/custodian accounts — an operator trust
 * class with NO on-chain solvency invariant, hence `solvency` ABSENT on
 * every row.
 *
 * Facts verified on-chain 2026-08-19 (probes + the audited public source
 * `bitwaylabs/bitway-earn`, Blocksec + Salus Oct 2025 — NB the deployed
 * vaults are NOT source-verified on any explorer; behavior was matched
 * against the repo):
 *
 *  - The LP token is a bare ERC-20 RECEIPT: every read and write lives on
 *    the parent vault, keyed by the underlying — and withdrawal rights
 *    live in the vault's internal `userAssetsInfo[msg.sender][token]`,
 *    which does NOT move with the LP token. A transferee cannot exit;
 *    the actions layer refuses `receiver != operator` on every leg.
 *  - Exit is `fee-or-queued`: `flashWithdrawWithPenalty` (0.5% on
 *    Absolute Return, 0% on Core Alpha — private slot-12 dials with no
 *    getter, pinned in the reader) capped by the vault's own thin token
 *    buffer, or the free `requestClaim` → 7-day `WAITING_TIME` → `claim`,
 *    with the payout FROZEN at the request-time rate (no yield during
 *    the wait). Cancel exists behind an admin flag (enabled live).
 *  - Admin surface is wide and untimelocked: pause blocks BOTH exits,
 *    the rate/penalty/waiting-time dials move without notice, and a
 *    per-address blacklist blocks stake and both exits.
 *  - Deposits: 5–10 token minimum per leg; Core Alpha additionally caps
 *    each USER's cumulative stake at ~1M per leg (a per-user gate, not a
 *    vault-wide capacity — so not `depositCapacity`).
 *
 * Deliberately omitted: the Absolute Return USDC leg (bwUSDC — supply 0
 * at integration; add it when funded, the Neutrl InstantUnstaking
 * precedent).
 */

/** [symbol, label, lpToken, underlying, underlyingSymbol, stakingVault, withdrawVault, strategyNote] */
const BITWAY_LEGS: [
  string,
  string,
  string,
  string,
  string,
  string,
  string,
  string,
][] = [
  [
    'bwUSDT',
    'Absolute Return USDT',
    '0xccafb706225331aedfec75b5347d462b98ed2fd2',
    '0x55d398326f99059ff775485246999027b3197955',
    'USDT',
    '0x5c4a6903732532eeb3ae0803e062d8ae25d52bd1',
    '0x2b8464ffb2b77de64cfefdeb00ad925576f157cb',
    'the Absolute Return strategy (0.5% instant-exit fee)',
  ],
  [
    'bwU',
    'Absolute Return U',
    '0xaa3d2534b4b87a2859e28c223f18265244ffffb7',
    '0xce24439f2d9c6a2289f741120fe202248b666666',
    'U',
    '0x5c4a6903732532eeb3ae0803e062d8ae25d52bd1',
    '0x2b8464ffb2b77de64cfefdeb00ad925576f157cb',
    'the Absolute Return strategy (0.5% instant-exit fee)',
  ],
  [
    'bwBTW',
    'Absolute Return BTW',
    '0x82356c921422a2202e0f96dfccc352598ca8ef71',
    '0x444045b0ee1ee319a660a5e3d604ca0ffa35acaa',
    'BTW',
    '0x5c4a6903732532eeb3ae0803e062d8ae25d52bd1',
    '0x2b8464ffb2b77de64cfefdeb00ad925576f157cb',
    "the Absolute Return strategy (0.5% instant-exit fee). The rate is denominated in BTW, Bitway's own volatile token — not in dollars",
  ],
  [
    'BTWUSDT',
    'Core Alpha USDT',
    '0x73af543d809c8d3414e5b92b3aa2c25b182ba3a1',
    '0x55d398326f99059ff775485246999027b3197955',
    'USDT',
    '0xb82e32062c773c7748776c06fdb11b92edae3b63',
    '0xfc8e6b1ab1a4c37724f8bc797730afa8d01d04ef',
    'the Core Alpha strategy (no instant-exit fee live; ~1M per-user stake cap)',
  ],
  [
    'BTWUSD1',
    'Core Alpha USD1',
    '0xb5c3617d4f077851cc6c7fae558d32e9782307f9',
    '0x8d0d000ee44948fc98c9b98a4fa4921476f08b0d',
    'USD1',
    '0xb82e32062c773c7748776c06fdb11b92edae3b63',
    '0xfc8e6b1ab1a4c37724f8bc797730afa8d01d04ef',
    'the Core Alpha strategy (no instant-exit fee live; ~1M per-user stake cap)',
  ],
  [
    'BTWU',
    'Core Alpha U',
    '0x4effb6bce5cad64d7162c7f7f15f557221b106d5',
    '0xce24439f2d9c6a2289f741120fe202248b666666',
    'U',
    '0xb82e32062c773c7748776c06fdb11b92edae3b63',
    '0xfc8e6b1ab1a4c37724f8bc797730afa8d01d04ef',
    'the Core Alpha strategy (no instant-exit fee live; ~1M per-user stake cap)',
  ],
]

export const BITWAY_ENTRIES: { [chainId: string]: SavingsRegistryEntry[] } = {
  [Chain.BNB_SMART_CHAIN_MAINNET]: BITWAY_LEGS.map(
    ([
      symbol,
      label,
      address,
      underlying,
      underlyingSymbol,
      stakingVault,
      withdrawVault,
      strategyNote,
    ]) => ({
      reader: 'bitway-vault' as const,
      address,
      underlying,
      symbol,
      label,
      brand: 'Bitway',
      description:
        `${symbol} is the receipt for ${underlyingSymbol} staked into Bitway Earn's ` +
        `${strategyNote}. Capital runs market-neutral trading in CEX/custodian ` +
        `accounts; yield settles at a fixed admin-set rate with no on-chain ` +
        `solvency invariant. Exit: free 7-day queue (payout frozen at the ` +
        `request-time rate) or an instant withdrawal capped by a small ` +
        `on-chain buffer. Withdrawal rights stay with the staking address — ` +
        `a transferee of the receipt token cannot exit.`,
      decimals: 18,
      underlyingDecimals: 18,
      isRebasing: false,
      isMintable: true,
      mintContract: stakingVault,
      accrual: 'linear' as const,
      withdrawalMode: 'fee-or-queued' as const,
      withdrawalCooldownSeconds: 604800,
      withdrawQueue: withdrawVault,
      // The instant leg pays from the staking vault's own token balance;
      // the reader treats `priceOracle` as "where the rate lives" (the
      // two-arg convertToAssets) — both are the parent vault.
      inventoryContract: stakingVault,
      priceOracle: stakingVault,
      yieldFetcher: bitwayFetcher,
      yieldKey: bitwayYieldKey(address),
    }),
  ),
}
